import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Delete } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import OfflineService from '../../services/OfflineService';
import AuthService from '../../services/AuthService';
import logo from '../../assets/logo-danes.png';
import bgPattern from '../../assets/fondo-danes.png';
import BrandLogo from '../common/BrandLogo';

const PadButton = ({ children, onClick, color, textColor }) => (
    <motion.button
        whileTap={{ scale: 0.92 }}
        onClick={onClick}
        style={{
            background: color || 'white',
            border: `1px solid ${color ? 'transparent' : '#e2e8f0'}`,
            borderRadius: '50%',
            width: '72px',
            height: '72px',
            fontSize: '1.6rem',
            fontWeight: '700',
            color: textColor || '#383636',
            boxShadow: '0 4px 10px rgba(0,0,0,0.07)',
            cursor: 'pointer',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Quicksand, sans-serif',
        }}
    >
        {children}
    </motion.button>
);

const TokenLogin = ({ onLogin }) => {
    const { updateSessionData, clearSessionData, authenticatedUser, updateAuthenticatedUser } = useAppContext();
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [setShowUserLogin] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [userLoginError, setUserLoginError] = useState('');
    const [userLoginLoading, setUserLoginLoading] = useState(false);

    useEffect(() => {
        OfflineService.clearSessionInfo();
        clearSessionData();
    }, [clearSessionData]);

    const handleUserLogin = async (e) => {
        e.preventDefault();
        setUserLoginError('');
        setUserLoginLoading(true);

        if (!username || !password) {
            setUserLoginError('Por favor, ingresa usuario y contraseña');
            setUserLoginLoading(false);
            return;
        }

        try {
            const userData = await AuthService.authenticateUser(username, password);
            updateAuthenticatedUser(userData);
            setShowUserLogin(false);
            setUsername('');
            setPassword('');
        } catch (err) {
            setUserLoginError(err.message || 'Error al autenticar usuario');
        } finally {
            setUserLoginLoading(false);
        }
    };

    const handleUserLogout = () => {
        updateAuthenticatedUser(null);
        setUsername('');
        setPassword('');
    };

    const handlePadClick = (num) => {
        if (pin.length < 4) { setPin(prev => prev + num); setError(''); }
    };
    const handlePadDelete = () => setPin(prev => prev.slice(0, -1));
    const handlePadClear = () => setPin('');

    const handleSubmit = async (e) => {
        e?.preventDefault();
        setError('');
        setLoading(true);

        if (!authenticatedUser) {
            setError('Por favor, primero inicia sesión como usuario de Odoo');
            setLoading(false);
            return;
        }

        if (!pin) {
            setError('Por favor, ingresa tu PIN');
            setLoading(false);
            return;
        }

        try {
            const responseData = await OfflineService.validateSession(pin);
            const sessionDataWithPin = {
                ...responseData,
                pin,
            };
            updateSessionData(sessionDataWithPin);
            OfflineService.saveSessionInfo(sessionDataWithPin, responseData.config_id);
            onLogin();
            navigate('/');
        } catch (err) {
            setError('Error de autenticación: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-screen" style={{ background: `linear-gradient(rgba(255,255,255,0.74), rgba(255,255,255,0.82)), url(${bgPattern})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
            <BrandLogo delay={0.3} />
            <div className="login-content">
                <div className="logo-container">
                    <img src={logo} alt="Danés" className="logo" />
                </div>

                {!authenticatedUser ? (
                    <div className="auth-card">
                        <h2 className="auth-title">Acceso de Personal</h2>
                        <p className="auth-subtitle">Ingresa con tu usuario Odoo</p>

                        <form onSubmit={handleUserLogin} className="auth-form">
                            <div className="form-group">
                                <label>Usuario</label>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Tu usuario"
                                    disabled={userLoginLoading}
                                />
                            </div>

                            <div className="form-group">
                                <label>Contraseña</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Tu contraseña"
                                    disabled={userLoginLoading}
                                />
                            </div>

                            {userLoginError && <p className="error-message">{userLoginError}</p>}

                            <button type="submit" className="btn-primary" disabled={userLoginLoading}>
                                {userLoginLoading ? 'Autenticando...' : 'Iniciar Sesión'}
                            </button>
                        </form>
                    </div>
                ) : (
                    <div className="auth-card pin-card">
                        <div className="user-info">
                            <div className="user-avatar">{authenticatedUser.name.charAt(0).toUpperCase()}</div>
                            <div className="user-details">
                                <p className="user-name">{authenticatedUser.name}</p>
                                <p className="user-username">@{authenticatedUser.username}</p>
                            </div>
                            <button onClick={handleUserLogout} className="btn-logout">
                                Cerrar Sesión
                            </button>
                        </div>

                        <div className="divider"></div>

                        <h3 className="pin-title">Ingresa tu PIN</h3>

                        {/* Puntos indicadores */}
                        <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', justifyContent: 'center' }}>
                            {[0, 1, 2, 3].map((i) => (
                                <motion.div
                                    key={i}
                                    animate={{
                                        scale: i < pin.length ? 1.2 : 1,
                                        backgroundColor: i < pin.length ? '#DA291C' : '#cbd5e1'
                                    }}
                                    style={{ width: '16px', height: '16px', borderRadius: '50%' }}
                                />
                            ))}
                        </div>

                        {error && <p className="error-message" style={{ marginBottom: '12px' }}>{error}</p>}

                        {/* Numpad */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', width: '100%', marginBottom: '16px' }}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                                <PadButton key={num} onClick={() => handlePadClick(num.toString())}>{num}</PadButton>
                            ))}
                            <PadButton onClick={handlePadClear} color="#fee2e2" textColor="#DA291C">C</PadButton>
                            <PadButton onClick={() => handlePadClick('0')}>0</PadButton>
                            <PadButton onClick={handlePadDelete} color="#e2e8f0" textColor="#383636"><Delete size={24} /></PadButton>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={handleSubmit}
                                disabled={pin.length !== 4 || loading}
                                className="btn-primary btn-activate"
                            >
                                {loading ? 'Verificando...' : 'Activar Dispositivo'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .login-screen {
                    position: fixed;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    overflow: hidden;
                }

                .login-content {
                    position: relative;
                    z-index: 10;
                    width: 90%;
                    max-width: 45rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1.2rem;
                    margin-top: -10rem;
                }

                .logo-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 100%;
                }

                .logo {
                    width: 100%;
                    max-width: 28rem;
                    height: auto;
                    filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.2));
                }

                .auth-card {
                    width: 100%;
                    background: white;
                    border-radius: 2rem;
                    padding: 3rem;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }

                .pin-card {
                    max-width: 420px;
                    margin: 0 auto;
                    padding: 3rem 2.5rem 3rem;
                }

                .btn-activate {
                    width: 80%;
                    margin-top: 4px;
                }

                .auth-title {
                    font-size: 2.5rem;
                    color: #383636;
                    font-weight: 700;
                    margin: 0 0 0.5rem 0;
                    font-family: 'Quicksand', sans-serif;
                }

                .auth-subtitle {
                    font-size: 1.2rem;
                    color: #666;
                    margin: 0 0 2rem 0;
                    font-family: 'Quicksand', sans-serif;
                }

                .auth-form {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .form-group label {
                    font-size: 1.1rem;
                    color: #383636;
                    font-weight: 600;
                    font-family: 'Quicksand', sans-serif;
                }

                .form-group input {
                    padding: 1.2rem;
                    font-size: 1.2rem;
                    border: 2px solid #e0e0e0;
                    border-radius: 1rem;
                    font-family: 'Quicksand', sans-serif;
                    transition: border-color 0.3s;
                }

                .form-group input:focus {
                    outline: none;
                    border-color: #DA291C;
                }

                .form-group input:disabled {
                    background: #f5f5f5;
                    cursor: not-allowed;
                }

                .user-info {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                }

                .user-avatar {
                    width: 4rem;
                    height: 4rem;
                    border-radius: 50%;
                    background: #DA291C;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.8rem;
                    font-weight: 700;
                    font-family: 'Quicksand', sans-serif;
                    flex-shrink: 0;
                }

                .user-details {
                    flex: 1;
                }

                .user-name {
                    font-size: 1.4rem;
                    font-weight: 700;
                    color: #383636;
                    margin: 0;
                    font-family: 'Quicksand', sans-serif;
                }

                .user-username {
                    font-size: 1rem;
                    color: #666;
                    margin: 0;
                    font-family: 'Quicksand', sans-serif;
                }

                .btn-logout {
                    padding: 0.8rem 1.5rem;
                    background: #f5f5f5;
                    color: #666;
                    border: none;
                    border-radius: 0.8rem;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    font-family: 'Quicksand', sans-serif;
                    transition: all 0.3s;
                    flex-shrink: 0;
                }

                .btn-logout:hover {
                    background: #e0e0e0;
                }

                .divider {
                    height: 1px;
                    background: #e0e0e0;
                    margin: 2rem 0;
                }

                .pin-title {
                    font-size: 1.8rem;
                    color: #383636;
                    font-weight: 700;
                    margin: 0 0 1.5rem 0;
                    font-family: 'Quicksand', sans-serif;
                }

                .pin-form {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .pin-input {
                    padding: 1.5rem;
                    font-size: 2rem;
                    text-align: center;
                    border: 2px solid #e0e0e0;
                    border-radius: 1rem;
                    font-family: 'Quicksand', sans-serif;
                    letter-spacing: 1rem;
                    transition: border-color 0.3s;
                }

                .pin-input:focus {
                    outline: none;
                    border-color: #DA291C;
                }

                .pin-input:disabled {
                    background: #f5f5f5;
                    cursor: not-allowed;
                }

                .btn-primary {
                    padding: 1.5rem;
                    background: #DA291C;
                    color: white;
                    border: none;
                    border-radius: 1rem;
                    font-size: 1.4rem;
                    font-weight: 700;
                    cursor: pointer;
                    font-family: 'Quicksand', sans-serif;
                    transition: all 0.3s;
                }

                .btn-primary:hover:not(:disabled) {
                    background: #007a36;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
                }

                .btn-primary:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }

                .error-message {
                    color: #e1251b;
                    font-size: 1rem;
                    margin: 0;
                    font-family: 'Quicksand', sans-serif;
                    font-weight: 600;
                }
            `}</style>
        </div>
    );
};

export default TokenLogin;